import type { ReactNode } from 'react';
import { MONO_MUTED } from '../charts';
import { TOKENS, TYPE } from '../theme';
import { staggerStyle } from './motion';

export interface ProportionItem {
  id: string;
  label: ReactNode;
  /** A second, quieter line under the label: "1,204 leads · 54% sold". */
  detail?: ReactNode;
  /** The figure on the right, already formatted. */
  value: ReactNode;
  /** A muted figure after the value, typically a percentage. */
  share?: ReactNode;
  /** Bar length, 0 to 1. */
  fraction: number;
  /** `positive` paints the bar green: delivered, sold, revenue. */
  tone?: 'ink' | 'positive' | 'muted';
  /** An explicit bar colour, e.g. a step of MONO_SCALE. Wins over `tone`. */
  color?: string;
}

const BAR_COLOR = {
  ink: TOKENS.text,
  positive: TOKENS.green,
  muted: MONO_MUTED,
} as const;

/**
 * Rows of label, figure and a thin proportion bar beneath, like the site's
 * "By source" and Reports rows. Reads more precisely than a donut: lengths on
 * a shared baseline are compared directly, angles are guessed.
 *
 * Bars grow in on mount and slide to new lengths when the data changes
 * (ui.css .hf-bar); both are off under reduced motion.
 */
export function ProportionList({
  items,
  rank = false,
  barHeight = 4,
}: {
  items: ProportionItem[];
  /** Number the rows 1, 2, 3 in a mono gutter. */
  rank?: boolean;
  barHeight?: number;
}) {
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 14 }}>
      {items.map((item, i) => (
        <li
          key={item.id}
          className="hf-rise"
          style={staggerStyle(i + 2, {
            display: 'grid',
            gridTemplateColumns: rank ? '18px minmax(0, 1fr)' : 'minmax(0, 1fr)',
            columnGap: 8,
          })}
        >
          {rank && (
            <span style={{ ...TYPE.monoFigure, fontSize: 11, color: TOKENS.textMuted, paddingTop: 1 }}>
              {i + 1}
            </span>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
              <span
                style={{
                  fontSize: 13, color: TOKENS.text, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flex: 'none' }}>
                <span style={{ ...TYPE.monoFigure, color: TOKENS.text }}>{item.value}</span>
                {item.share !== undefined && (
                  <span style={{ ...TYPE.monoFigure, color: TOKENS.textMuted, minWidth: 40, textAlign: 'right' }}>
                    {item.share}
                  </span>
                )}
              </span>
            </div>
            {item.detail && (
              <div style={{ fontSize: 11.5, color: TOKENS.textMuted, marginTop: 2 }}>{item.detail}</div>
            )}
            <div
              style={{
                marginTop: 7, height: barHeight, borderRadius: barHeight,
                background: TOKENS.surface2, overflow: 'hidden',
              }}
            >
              <div
                className="hf-bar"
                style={staggerStyle(i, {
                  height: '100%',
                  borderRadius: barHeight,
                  background: item.color ?? BAR_COLOR[item.tone ?? 'ink'],
                  transform: `scaleX(${Math.max(0, Math.min(1, item.fraction))})`,
                })}
              />
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
